import { useEffect, useState, useCallback, useRef } from "react";
import { PageTitle } from "@/components/PageHeader";
import { api, API } from "@/lib/api";
import { useCachedState } from "@/lib/swr";
import { useDash } from "./ctx";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ESignQuickSend } from "@/components/ESignQuickSend";
import {
  PenLine, Plus, X, CheckCircle2, XCircle, Clock, Send, Download,
  BellRing, FileText, Upload, ChevronLeft, ChevronRight, ChevronUp, ChevronDown,
  Users, Ban, Loader2, ListOrdered, Hourglass, ZoomIn, ZoomOut, Eye, CalendarClock,
  Copy, Type, CheckSquare, Archive, MailWarning, Trash2,
} from "lucide-react";
import { V } from "@/lib/vocab";

export const SIGNER_COLORS = ["#e5342e", "#2563eb", "#16a34a", "#d97706", "#7c3aed", "#0891b2"];
export const FIELD_TYPES = [
  { kind: "signature", label: "Signature", w: 0.26, h: 0.06 },
  { kind: "initials", label: "Initials", w: 0.10, h: 0.05 },
  { kind: "date", label: "Date", w: 0.14, h: 0.045 },
  { kind: "text", label: "Text", w: 0.22, h: 0.045 },
  { kind: "checkbox", label: "Checkbox", w: 0.028, h: 0.022 },
];
const FIELD_SHORT = { signature: "Sign", initials: "Init", date: "Date", text: "Text", checkbox: "Check" };
const STATUS = {
  draft: { label: "DRAFT", cls: "bg-sand-100 text-ink/60" },
  active: { label: "OUT FOR SIGNATURE", cls: "bg-amber-50 text-amber-700" },
  batch: { label: "OUT · EACH SIGNS OWN COPY", cls: "bg-amber-50 text-amber-700" },
  completed: { label: "COMPLETED", cls: "bg-emerald-50 text-emerald-700" },
  declined: { label: "DECLINED", cls: "bg-danger-50 text-danger-700" },
  expired: { label: "EXPIRED", cls: "bg-sand-100 text-ink/50" },
  canceled: { label: "CANCELED", cls: "bg-sand-100 text-ink/40 line-through" },
};
const fmtDT = (s) => { try { return new Date(s).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return s || ""; } };
// Deadlines are a DAY the sender picked, not an instant — format the date-only string so no timezone can roll it.
export const fmtDay = (s) => {
  if (!s) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(s);
  try { return d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }); } catch { return s; }
};
const deadlineOf = (r) => r?.expires_on || (r?.expires_at ? r.expires_at.slice(0, 10) : "");
export const todayISO = () => new Date().toISOString().slice(0, 10);
const contactKey = (p) => ((p.phone || "").replace(/\D/g, "") || (p.email || "").toLowerCase());

export function Toggle({ on, onClick, testid }) {
  return (
    <button data-testid={testid} type="button" onClick={onClick}
      className={`relative h-5 w-9 rounded-full transition-colors ${on ? "bg-ink" : "bg-sand-200"}`}>
      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${on ? "left-[18px]" : "left-0.5"}`} />
    </button>
  );
}

// ─── Click-to-place fields on the PDF pages ──────────────────────────────────
export function PlacementEditor({ tenantId, req, signers, setSigners, selected, setSelected, singleSlot, initialPage = 1 }) {
  const [page, setPage] = useState(Math.min(Math.max(1, initialPage), req?.page_count || 1));
  const [tool, setTool] = useState("signature");
  const [zoom, setZoom] = useState(1);
  const [editing, setEditing] = useState(null); // {si, fj} — a text/checkbox chip whose label/required is being set
  const imgRef = useRef(null);
  const dragRef = useRef(null);

  const placeField = (e) => {
    if (selected == null || !signers[selected]) { toast.info("Pick a signer on the left, then click where their field goes."); return; }
    const rect = imgRef.current.getBoundingClientRect();
    const spec = FIELD_TYPES.find((t) => t.kind === tool);
    const x = Math.min(Math.max((e.clientX - rect.left) / rect.width - spec.w / 2, 0), 1 - spec.w);
    const y = Math.min(Math.max((e.clientY - rect.top) / rect.height - spec.h / 2, 0), 1 - spec.h);
    const fj = (signers[selected].fields || []).length;
    const extra = tool === "text" ? { label: "", required: true } : tool === "checkbox" ? { label: "", required: false } : {};
    setSigners((prev) => prev.map((s, i) => (i === selected
      ? { ...s, fields: [...(s.fields || []), { kind: tool, page, x, y, w: spec.w, h: spec.h, ...extra }] }
      : s)));
    if (tool === "text" || tool === "checkbox") setEditing({ si: selected, fj });
  };
  const removeField = (signerIdx, fieldIdx) => {
    setEditing(null);
    setSigners((prev) => prev.map((s, i) => (i === signerIdx
      ? { ...s, fields: s.fields.filter((_, j) => j !== fieldIdx) }
      : s)));
  };
  const updateField = (si, fj, patch) => setSigners((prev) => prev.map((s, i) => (i === si
    ? { ...s, fields: s.fields.map((f, j) => (j === fj ? { ...f, ...patch } : f)) }
    : s)));
  // Drag a placed chip to move it; a click without movement on a text/checkbox chip opens its options.
  const startDrag = (e, si, fj, f) => {
    e.stopPropagation(); e.preventDefault();
    const rect = imgRef.current.getBoundingClientRect();
    dragRef.current = { si, fj, sx: e.clientX, sy: e.clientY, moved: false,
      dx: (e.clientX - rect.left) / rect.width - f.x,
      dy: (e.clientY - rect.top) / rect.height - f.y };
    e.currentTarget.setPointerCapture(e.pointerId);
    setSelected(si);
  };
  const onDrag = (e) => {
    const d = dragRef.current;
    if (!d) return;
    if (!d.moved && Math.hypot(e.clientX - d.sx, e.clientY - d.sy) < 4) return;
    d.moved = true;
    const rect = imgRef.current.getBoundingClientRect();
    setSigners((prev) => prev.map((s, i) => (i === d.si
      ? { ...s, fields: s.fields.map((f, j) => (j === d.fj
          ? { ...f,
              x: Math.min(Math.max((e.clientX - rect.left) / rect.width - d.dx, 0), 1 - f.w),
              y: Math.min(Math.max((e.clientY - rect.top) / rect.height - d.dy, 0), 1 - f.h) }
          : f)) }
      : s)));
  };
  const endDrag = () => {
    const d = dragRef.current;
    dragRef.current = null;
    if (!d || d.moved) return;
    const f = signers[d.si]?.fields?.[d.fj];
    if (f && (f.kind === "text" || f.kind === "checkbox")) setEditing({ si: d.si, fj: d.fj });
  };

  const edField = editing ? signers[editing.si]?.fields?.[editing.fj] : null;
  useEffect(() => { if (editing && !edField) setEditing(null); }, [editing, edField]);

  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-ink/40 uppercase tracking-wide">Place:</span>
          <div className="flex rounded-full bg-white border border-sand-200 p-0.5">
            {FIELD_TYPES.map((t) => (
              <button key={t.kind} data-testid={`sig-tool-${t.kind}`} onClick={() => setTool(t.kind)}
                className={`rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${tool === t.kind ? "bg-ink text-white" : "text-ink/50 hover:text-ink"}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <button data-testid="sig-zoom-out" disabled={zoom <= 0.5} onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))}
              className="rounded-full border border-sand-200 p-1.5 disabled:opacity-30 hover:bg-sand-50" title="Zoom out"><ZoomOut className="h-4 w-4" /></button>
            <span data-testid="sig-zoom-level" className="text-xs font-bold text-ink/50 w-11 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
            <button data-testid="sig-zoom-in" disabled={zoom >= 2.5} onClick={() => setZoom((z) => Math.min(2.5, +(z + 0.25).toFixed(2)))}
              className="rounded-full border border-sand-200 p-1.5 disabled:opacity-30 hover:bg-sand-50" title="Zoom in"><ZoomIn className="h-4 w-4" /></button>
          </div>
          {req.page_count > 1 && (
            <div className="flex items-center gap-2 text-sm font-semibold">
              <button data-testid="sig-page-prev" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                className="rounded-full border border-sand-200 p-1.5 disabled:opacity-30 hover:bg-sand-50"><ChevronLeft className="h-4 w-4" /></button>
              Page {page} / {req.page_count}
              <button data-testid="sig-page-next" disabled={page >= req.page_count} onClick={() => setPage((p) => p + 1)}
                className="rounded-full border border-sand-200 p-1.5 disabled:opacity-30 hover:bg-sand-50"><ChevronRight className="h-4 w-4" /></button>
            </div>
          )}
        </div>
      </div>
      <p className="text-xs text-ink/45 mb-2">
        Click the document to drop a <span className="font-bold">{FIELD_TYPES.find((t) => t.kind === tool).label}</span> field for {singleSlot ? "every recipient" : "the selected signer"} — as many as you need, on any page. <span className="font-bold">Drag a field to move it.</span> Dates fill in automatically; <span className="font-bold">Text</span> and <span className="font-bold">Checkbox</span> are filled in by the signer — click a placed one to set its label.
      </p>
      {edField && (
        <div data-testid="sig-field-options" className="flex flex-wrap items-center gap-3 rounded-xl border border-ink/25 bg-white px-3 py-2 mb-2">
          {edField.kind === "text" ? <Type className="h-4 w-4 text-ink/60" /> : <CheckSquare className="h-4 w-4 text-ink/60" />}
          <span className="text-xs font-bold text-ink/70">{edField.kind === "text" ? "Text field" : "Checkbox"}{!singleSlot && ` · ${(signers[editing.si].name || `Signer ${editing.si + 1}`).split(" ")[0]}`}</span>
          <input data-testid="sig-field-label" autoFocus value={edField.label || ""} maxLength={40}
            onChange={(e) => updateField(editing.si, editing.fj, { label: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && setEditing(null)}
            placeholder={edField.kind === "text" ? 'Label the signer sees — e.g. "Title", "Unit #", "Printed name"' : 'Label — e.g. "I agree to the amendment"'}
            className="flex-1 min-w-[200px] rounded-lg border border-sand-200 px-2.5 py-1.5 text-sm outline-none focus:border-ink/40" />
          <label className="flex items-center gap-2 text-xs font-semibold text-ink/70 cursor-pointer">
            <Toggle testid="sig-field-required" on={!!edField.required} onClick={() => updateField(editing.si, editing.fj, { required: !edField.required })} />
            Required
          </label>
          <button data-testid="sig-field-remove-current" onClick={() => removeField(editing.si, editing.fj)}
            className="text-xs font-semibold text-ink/40 hover:text-danger-600">Remove</button>
          <button data-testid="sig-field-options-done" onClick={() => setEditing(null)}
            className="rounded-full bg-ink text-white text-xs font-bold px-3 py-1.5">Done</button>
        </div>
      )}
      <div className="border border-sand-200 rounded-xl bg-sand-100 shadow-sm overflow-auto max-h-[72vh]" data-testid="sig-place-scroll">
        <div className="relative select-none bg-white mx-auto" style={{ width: `${zoom * 100}%` }}>
          <img ref={imgRef} data-testid="sig-place-page" alt={`Page ${page}`} draggable={false}
            src={`${API}/tenants/${tenantId}/sign-requests/${req.id}/page/${page}`}
            className="w-full cursor-crosshair" onClick={placeField} />
          {signers.map((s, i) => (s.fields || []).map((f, j) => f.page === page && (
            <div key={`${i}-${j}`} data-testid={`sig-field-chip-${i}-${j}`}
              onPointerDown={(e) => startDrag(e, i, j, f)} onPointerMove={onDrag}
              onPointerUp={endDrag} onPointerCancel={endDrag}
              className={`absolute rounded-md border-2 flex items-center justify-center text-[10px] font-bold group cursor-move ${editing && editing.si === i && editing.fj === j ? "ring-2 ring-ink ring-offset-1" : ""}`}
              style={{
                left: `${f.x * 100}%`, top: `${f.y * 100}%`, width: `${f.w * 100}%`, height: `${f.h * 100}%`,
                borderColor: SIGNER_COLORS[i % SIGNER_COLORS.length],
                background: `${SIGNER_COLORS[i % SIGNER_COLORS.length]}18`,
                color: SIGNER_COLORS[i % SIGNER_COLORS.length],
                touchAction: "none",
              }}>
              <span className="truncate px-1 pointer-events-none">
                {f.kind === "signature" ? <PenLine className="h-3 w-3 inline mr-0.5" /> : null}
                {f.kind === "checkbox" ? "☐" : (f.label || FIELD_SHORT[f.kind])}{!singleSlot && f.kind !== "checkbox" && ` · ${(s.name || `#${i + 1}`).split(" ")[0]}`}
              </span>
              <button data-testid={`sig-field-remove-${i}-${j}`}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); removeField(i, j); }}
                className="absolute -top-2 -right-2 h-4 w-4 rounded-full bg-ink text-white items-center justify-center hidden group-hover:flex">
                <X className="h-2.5 w-2.5" />
              </button>
            </div>
          )))}
        </div>
      </div>
    </div>
  );
}

// ─── shared: board & team quick-add chips, roster search ─────────────────────
export function useTeam(tenantId) {
  const [team, setTeam] = useState([]);
  useEffect(() => {
    api.get("/admins", { params: { tenant_id: tenantId } })
      .then((r) => setTeam((r.data || [])
        .filter((u) => ["board_member", "hoa_manager"].includes(u.role))
        .filter((u) => (u.phone || u.email))))
      .catch(() => {});
  }, [tenantId]);
  return team;
}
export const teamLabel = (u) => {
  const raw = (u.name || "").trim();
  return (!raw || raw.includes("@"))
    ? ((u.email || raw).split("@")[0].replace(/[._-]+/g, " ").trim() || "Teammate").replace(/\b\w/g, (c) => c.toUpperCase())
    : raw;
};
export function TeamChips({ team, onPick, prefix }) {
  if (!team.length) return null;
  return (
    <div>
      <p className="text-[11px] font-bold text-ink/40 uppercase tracking-wide mb-1.5">Quick add — {V.grp()} & team</p>
      <div className="flex flex-wrap gap-1.5">
        {team.map((u) => {
          const label = teamLabel(u);
          return (
            <button key={u.user_id} data-testid={`${prefix}-team-${u.user_id}`}
              onClick={() => onPick({ name: label, phone: u.phone || "", email: u.email || "" })}
              title={`${label} · ${u.role === "board_member" ? `${V.one("board")}` : "Manager"}${u.email ? ` · ${u.email}` : ""}`}
              className="rounded-full bg-sand-100 hover:bg-sand-200 text-xs font-semibold px-3 py-1.5">
              {label}<span className="text-ink/40 font-normal ml-1">{u.role === "board_member" ? `${V.grp()}` : "mgr"}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
export function RosterSearch({ tenantId, onPick, prefix, label = "Quick add — residents" }) {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  useEffect(() => {
    if (!q.trim()) { setHits([]); return; }
    const t = setTimeout(() => {
      api.get(`/tenants/${tenantId}/people/page`, { params: { q, page_size: 5 } })
        .then((r) => setHits(r.data?.items || [])).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [q, tenantId]);
  return (
    <div>
      <p className="text-[11px] font-bold text-ink/40 uppercase tracking-wide mb-1.5">{label}</p>
      <input data-testid={`${prefix}-resident-search`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the roster…"
        className="w-full rounded-xl border border-sand-200 px-3 py-2 text-sm outline-none focus:border-ink/30" />
      {hits.length > 0 && (
        <div className="mt-1.5 rounded-xl border border-sand-200 divide-y divide-sand-100 overflow-hidden bg-white">
          {hits.map((p) => (
            <button key={p.id} data-testid={`${prefix}-resident-${p.id}`}
              onClick={() => { onPick({ name: p.name, phone: p.phone || "", email: p.email || "", unit: p.household?.unit || "" }); setQ(""); setHits([]); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-sand-50">
              <span className="font-semibold">{p.name}</span>
              <span className="text-ink/40 text-xs ml-2">{p.household?.unit ? `${p.household.unit} · ` : ""}{p.phone || p.email || "no contact"}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Signer rows ──────────────────────────────────────────────────────────────
function SignerPanel({ tenantId, signers, setSigners, selected, setSelected, sequential, setSequential }) {
  const team = useTeam(tenantId);
  const addSigner = (s = { name: "", phone: "", email: "" }) => {
    if (signers.length >= 30) { toast.info("Up to 30 signers on one document — for more, use “each signs their own copy”."); return; }
    if (s.name && signers.some((x) => x.name === s.name && (x.phone === (s.phone || "") || x.email === (s.email || "")))) {
      toast.info(`${s.name} is already a signer`); return;
    }
    setSigners((prev) => [...prev, { name: s.name || "", phone: s.phone || "", email: s.email || "", fields: [] }]);
    setSelected(signers.length);
  };
  const update = (i, k, v) => setSigners((prev) => prev.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  const remove = (i) => { setSigners((prev) => prev.filter((_, j) => j !== i)); setSelected(null); };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= signers.length) return;
    setSigners((prev) => { const a = [...prev]; [a[i], a[j]] = [a[j], a[i]]; return a; });
    setSelected(j);
  };
  const fieldSummary = (s) => {
    const c = {};
    (s.fields || []).forEach((f) => { c[f.kind] = (c[f.kind] || 0) + 1; });
    if (!c.signature) return null;
    const parts = [`${c.signature} sig`];
    if (c.initials) parts.push(`${c.initials} init`);
    if (c.date) parts.push(`${c.date} date`);
    if (c.text) parts.push(`${c.text} text`);
    if (c.checkbox) parts.push(`${c.checkbox} box`);
    return parts.join(" · ");
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="font-heading font-extrabold text-ink flex items-center gap-2"><Users className="h-4 w-4" /> Signers</div>
        <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-ink/60" title="Each signer is invited only after the one before them signs">
          <ListOrdered className="h-4 w-4" />
          Sign in order
          <Toggle testid="sig-sequential-toggle" on={sequential} onClick={() => setSequential(!sequential)} />
        </label>
      </div>
      {sequential && signers.length > 0 && (
        <p className="text-[11px] text-ink/45 -mt-1">Invites go out one at a time, top to bottom — #2 hears from us only after #1 signs.</p>
      )}
      {signers.map((s, i) => (
        <div key={i} data-testid={`signer-row-${i}`} onClick={() => setSelected(i)}
          className={`rounded-2xl border p-3 space-y-2 cursor-pointer transition-colors ${selected === i ? "border-ink shadow-sm" : "border-sand-200 hover:border-sand-300"}`}>
          <div className="flex items-center gap-2">
            {sequential ? (
              <span className="h-5 w-5 rounded-full text-[10px] font-black text-white flex items-center justify-center shrink-0"
                style={{ background: SIGNER_COLORS[i % SIGNER_COLORS.length] }}>{i + 1}</span>
            ) : (
              <span className="h-3 w-3 rounded-full shrink-0" style={{ background: SIGNER_COLORS[i % SIGNER_COLORS.length] }} />
            )}
            <input data-testid={`signer-name-${i}`} value={s.name} onChange={(e) => update(i, "name", e.target.value)}
              placeholder="Full name" className="flex-1 min-w-0 bg-transparent text-sm font-semibold outline-none placeholder:font-normal" />
            {fieldSummary(s)
              ? <span className="text-[10px] font-bold text-emerald-600 shrink-0">{fieldSummary(s)}</span>
              : <span className="text-[10px] font-bold text-amber-600 shrink-0">NEEDS SIGNATURE</span>}
            {sequential && (
              <span className="flex flex-col shrink-0">
                <button data-testid={`signer-up-${i}`} onClick={(e) => { e.stopPropagation(); move(i, -1); }} disabled={i === 0}
                  className="text-ink/30 hover:text-ink disabled:opacity-20"><ChevronUp className="h-3.5 w-3.5" /></button>
                <button data-testid={`signer-down-${i}`} onClick={(e) => { e.stopPropagation(); move(i, 1); }} disabled={i === signers.length - 1}
                  className="text-ink/30 hover:text-ink disabled:opacity-20"><ChevronDown className="h-3.5 w-3.5" /></button>
              </span>
            )}
            <button data-testid={`signer-remove-${i}`} onClick={(e) => { e.stopPropagation(); remove(i); }} className="text-ink/30 hover:text-danger-500"><X className="h-4 w-4" /></button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input data-testid={`signer-phone-${i}`} value={s.phone} onChange={(e) => update(i, "phone", e.target.value)}
              placeholder="Mobile (SMS)" className="rounded-lg border border-sand-200 px-2 py-1.5 text-xs outline-none focus:border-ink/30" />
            <input data-testid={`signer-email-${i}`} value={s.email} onChange={(e) => update(i, "email", e.target.value)}
              placeholder="Email" className="rounded-lg border border-sand-200 px-2 py-1.5 text-xs outline-none focus:border-ink/30" />
          </div>
        </div>
      ))}
      <button data-testid="signer-add-blank" onClick={() => addSigner()}
        className="w-full rounded-2xl border border-dashed border-sand-300 py-2.5 text-sm font-semibold text-ink/60 hover:border-ink/40 hover:text-ink flex items-center justify-center gap-1.5">
        <Plus className="h-4 w-4" /> Add signer
      </button>
      <TeamChips team={team} onPick={addSigner} prefix="quickadd" />
      <RosterSearch tenantId={tenantId} onPick={addSigner} prefix="quickadd" />
    </div>
  );
}

// ─── CC / viewers — get the signed copy, don't sign ──────────────────────────
export function ViewerPanel({ tenantId, viewers, setViewers }) {
  const team = useTeam(tenantId);
  const add = (v = { name: "", phone: "", email: "" }) => {
    if (viewers.length >= 10) { toast.info("Up to 10 people can be copied."); return; }
    if (v.name && viewers.some((x) => x.name === v.name)) { toast.info(`${v.name} is already copied`); return; }
    setViewers((prev) => [...prev, { name: v.name || "", phone: v.phone || "", email: v.email || "" }]);
  };
  const update = (i, k, val) => setViewers((prev) => prev.map((v, j) => (j === i ? { ...v, [k]: val } : v)));
  return (
    <div className="space-y-2 rounded-2xl border border-sand-200 bg-white/60 p-3" data-testid="sig-viewers">
      <div className="flex items-center justify-between">
        <div className="text-sm font-extrabold text-ink flex items-center gap-2"><Eye className="h-4 w-4" /> Copy (view only)</div>
        <span className="text-[11px] text-ink/45">Gets the signed packet — doesn&apos;t sign</span>
      </div>
      {viewers.map((v, i) => (
        <div key={i} data-testid={`viewer-row-${i}`} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-1.5 items-center">
          <input data-testid={`viewer-name-${i}`} value={v.name} onChange={(e) => update(i, "name", e.target.value)} placeholder="Name"
            className="rounded-lg border border-sand-200 px-2 py-1.5 text-xs outline-none focus:border-ink/30 min-w-0" />
          <input data-testid={`viewer-phone-${i}`} value={v.phone} onChange={(e) => update(i, "phone", e.target.value)} placeholder="Mobile"
            className="rounded-lg border border-sand-200 px-2 py-1.5 text-xs outline-none focus:border-ink/30 min-w-0" />
          <input data-testid={`viewer-email-${i}`} value={v.email} onChange={(e) => update(i, "email", e.target.value)} placeholder="Email"
            className="rounded-lg border border-sand-200 px-2 py-1.5 text-xs outline-none focus:border-ink/30 min-w-0" />
          <button data-testid={`viewer-remove-${i}`} onClick={() => setViewers((prev) => prev.filter((_, j) => j !== i))} className="text-ink/30 hover:text-redline-500"><X className="h-4 w-4" /></button>
        </div>
      ))}
      <div className="flex flex-wrap gap-1.5 items-center">
        <button data-testid="viewer-add-blank" onClick={() => add()}
          className="rounded-full border border-dashed border-sand-300 px-3 py-1.5 text-xs font-semibold text-ink/60 hover:border-ink/40 hover:text-ink flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Add</button>
        {team.map((u) => (
          <button key={u.user_id} data-testid={`viewer-team-${u.user_id}`} onClick={() => add({ name: teamLabel(u), phone: u.phone || "", email: u.email || "" })}
            className="rounded-full bg-sand-100 hover:bg-sand-200 text-xs font-semibold px-3 py-1.5">{teamLabel(u)}</button>
        ))}
      </div>
    </div>
  );
}

// ─── Bulk recipients — each signs their own copy ─────────────────────────────
function RecipientPanel({ tenantId, recipients, setRecipients }) {
  const team = useTeam(tenantId);
  const [counts, setCounts] = useState(null);
  const [busy, setBusy] = useState("");
  const [manual, setManual] = useState({ name: "", phone: "", email: "" });
  useEffect(() => {
    api.get(`/tenants/${tenantId}/sign-recipients`, { params: { scope: "households" } })
      .then((r) => setCounts(r.data?.counts || null)).catch(() => setCounts({}));
  }, [tenantId]);

  const merge = (rows) => {
    setRecipients((prev) => {
      const have = new Set(prev.map(contactKey));
      const added = [];
      let shared = 0;
      for (const r of rows) {
        const k = contactKey(r);
        if (!(r.name || "").trim() || !k) continue;
        if (have.has(k)) { shared += 1; continue; }
        have.add(k);
        added.push({ name: r.name, phone: r.phone || "", email: r.email || "", unit: r.unit || "" });
      }
      if (rows.length && !added.length) toast.info(shared ? "Everyone in that group is already on the list (or shares a phone/email with someone who is)" : "Nobody reachable in that group");
      else if (added.length) toast.success(`Added ${added.length} recipient${added.length === 1 ? "" : "s"}${shared ? ` · ${shared} skipped — same phone/email as someone already listed (one copy per contact)` : ""}`);
      return [...prev, ...added];
    });
  };
  const addScope = async (scope) => {
    setBusy(scope);
    try {
      const r = await api.get(`/tenants/${tenantId}/sign-recipients`, { params: { scope } });
      merge(r.data?.recipients || []);
    } catch { toast.error("Couldn't load the roster"); }
    setBusy("");
  };
  const addManual = () => {
    if (!manual.name.trim()) { toast.error("Name is required"); return; }
    if (!manual.phone.trim() && !manual.email.trim()) { toast.error("Add a mobile or email"); return; }
    merge([manual]);
    setManual({ name: "", phone: "", email: "" });
  };
  const SCOPES = [["owners", "All owners"], ["households", "One per home"], ["everyone", "Everyone"]];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="font-heading font-extrabold text-ink flex items-center gap-2"><Copy className="h-4 w-4" /> Recipients</div>
        <span data-testid="recipient-count" className="text-xs font-bold text-ink/60 tabular-nums">{recipients.length} {recipients.length === 1 ? "copy" : "copies"}</span>
      </div>
      <p className="text-[11px] text-ink/45 -mt-1">Each person gets their <span className="font-bold">own copy</span> to sign — same fields on every copy. Up to 500.</p>
      <div className="flex flex-wrap gap-1.5">
        {SCOPES.map(([k, label]) => (
          <button key={k} data-testid={`recipient-scope-${k}`} onClick={() => addScope(k)} disabled={!!busy}
            className="rounded-full bg-ink text-white text-xs font-bold px-3 py-1.5 hover:bg-ink-700 disabled:opacity-50 flex items-center gap-1.5">
            {busy === k ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
            {label}{counts && counts[k] != null ? <span className="font-normal opacity-70">({counts[k]})</span> : null}
          </button>
        ))}
      </div>
      <RosterSearch tenantId={tenantId} onPick={(p) => merge([p])} prefix="recipient" label={`Add one ${V.lc("person")}`} />
      <TeamChips team={team} onPick={(p) => merge([p])} prefix="recipient" />
      <div className="rounded-2xl border border-sand-200 bg-white/60 p-2.5 space-y-1.5" data-testid="recipient-manual">
        <p className="text-[11px] font-bold text-ink/40 uppercase tracking-wide">Add someone by hand</p>
        <div className="grid grid-cols-3 gap-1.5">
          <input data-testid="recipient-manual-name" value={manual.name} onChange={(e) => setManual({ ...manual, name: e.target.value })} placeholder="Name" className="rounded-lg border border-sand-200 px-2 py-1.5 text-xs outline-none focus:border-ink/30 min-w-0" />
          <input data-testid="recipient-manual-phone" value={manual.phone} onChange={(e) => setManual({ ...manual, phone: e.target.value })} placeholder="Mobile" className="rounded-lg border border-sand-200 px-2 py-1.5 text-xs outline-none focus:border-ink/30 min-w-0" />
          <input data-testid="recipient-manual-email" value={manual.email} onChange={(e) => setManual({ ...manual, email: e.target.value })} placeholder="Email" className="rounded-lg border border-sand-200 px-2 py-1.5 text-xs outline-none focus:border-ink/30 min-w-0" />
        </div>
        <button data-testid="recipient-manual-add" onClick={addManual} className="text-xs font-bold text-ink/70 hover:text-ink flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Add</button>
      </div>
      {recipients.length > 0 && (
        <div className="rounded-2xl border border-sand-200 bg-white divide-y divide-sand-100 max-h-72 overflow-y-auto" data-testid="recipient-list">
          {recipients.map((r, i) => (
            <div key={`${contactKey(r)}-${i}`} data-testid={`recipient-row-${i}`} className="flex items-center gap-2 px-3 py-1.5 text-xs">
              <span className="font-semibold truncate flex-1">{r.name}</span>
              {r.unit && <span className="text-ink/40 shrink-0">{r.unit}</span>}
              <span className="text-ink/40 truncate max-w-[40%]">{r.phone || r.email}</span>
              <button data-testid={`recipient-remove-${i}`} onClick={() => setRecipients((prev) => prev.filter((_, j) => j !== i))} className="text-ink/30 hover:text-redline-500 shrink-0"><X className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      )}
      {recipients.length > 0 && (
        <button data-testid="recipient-clear" onClick={() => setRecipients([])} className="text-xs font-semibold text-ink/40 hover:text-ink">Clear list</button>
      )}
    </div>
  );
}

// ─── New-request wizard (create draft → signers + placement → send) ──────────
function NewRequestWizard({ tenantId, tenant, onClose, onSent, resumeDraft }) {
  const [step, setStep] = useState(resumeDraft ? 2 : 1);
  const [mode, setMode] = useState(resumeDraft?.batch_count ? "bulk" : "single");
  const [title, setTitle] = useState(resumeDraft?.title || "");
  const [message, setMessage] = useState(resumeDraft?.message || "");
  const [docs, setDocs] = useState([]);
  const [pickedDoc, setPickedDoc] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [req, setReq] = useState(resumeDraft || null);
  const [signers, setSigners] = useState(
    (resumeDraft?.parties || []).map((p) => ({ name: p.name || "", phone: p.phone || "", email: p.email || "", fields: p.fields || [] })));
  const [viewers, setViewers] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [bulkFields, setBulkFields] = useState([]);
  const [selected, setSelected] = useState(null);
  const [sequential, setSequential] = useState(false);
  // Defaults come from Settings → Features → E-Sign (module settings); the sender can still change them per request.
  const [cadence, setCadence] = useState([1, 2, 3, 7].includes(tenant?.signatures_reminder_every_days) ? tenant.signatures_reminder_every_days : 2);
  const [maxRem, setMaxRem] = useState(typeof tenant?.signatures_reminder_max === "number" ? tenant.signatures_reminder_max : 5);
  const [expires, setExpires] = useState("");
  const [notifyOnSign, setNotifyOnSign] = useState(tenant?.signatures_notify_on_sign === true);
  const [notifyOnDecline, setNotifyOnDecline] = useState(tenant?.signatures_notify_on_decline !== false);

  useEffect(() => {
    api.get(`/tenants/${tenantId}/documents`).then((r) =>
      setDocs((r.data || []).filter((d) => (d.filename || "").toLowerCase().endsWith(".pdf") && d.storage_path))
    ).catch(() => {});
  }, [tenantId]);

  const createDraft = async () => {
    if (!title.trim()) { toast.error("Give this request a title"); return; }
    if (!file && !pickedDoc) { toast.error("Attach a PDF or pick a library document"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("message", message);
      if (pickedDoc) fd.append("document_id", pickedDoc);
      else fd.append("file", file);
      const r = await api.post(`/tenants/${tenantId}/sign-requests`, fd);
      setReq(r.data);
      setStep(2);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't read that PDF"); }
    setBusy(false);
  };

  const cleanViewers = () => viewers.filter((v) => (v.name || "").trim() && ((v.phone || "").trim() || (v.email || "").trim()));
  const opts = { message, reminder_every_days: cadence, reminder_max: maxRem, expires_at: expires || null };

  const send = async () => {
    if (!req?.id) { toast.error("The draft didn't save — go back and re-attach the document"); return; }
    if (!signers.length) { toast.error("Add at least one signer"); return; }
    for (const s of signers) {
      if (!(s.name || "").trim()) { toast.error("Every signer needs a name"); return; }
      if (!(s.phone || "").trim() && !(s.email || "").trim()) { toast.error(`${s.name} needs a mobile number or email`); return; }
      if (!(s.fields || []).some((f) => f.kind === "signature")) { toast.error(`Place a signature field for ${s.name} — click the document`); return; }
    }
    for (const v of viewers) {
      if ((v.name || "").trim() && !(v.phone || "").trim() && !(v.email || "").trim()) { toast.error(`${v.name} (copied) needs a mobile or email`); return; }
    }
    setBusy(true);
    try {
      await api.post(`/tenants/${tenantId}/sign-requests/${req.id}/send`, {
        signers, viewers: cleanViewers(), sequential, notify_on_sign: notifyOnSign, notify_on_decline: notifyOnDecline, ...opts,
      });
      toast.success(sequential
        ? `Sent! ${signers[0].name} is up first — each signature invites the next signer automatically.`
        : "Sent! Every signer just got a text and/or email with their signing link.");
      onSent();
    } catch (e) {
      console.error("signature send failed", e);
      toast.error(e?.response?.data?.detail || e?.message || "Couldn't send — your draft is saved, try again");
      setBusy(false);
    }
  };

  const sendBatch = async () => {
    if (!req?.id) { toast.error("The draft didn't save — go back and re-attach the document"); return; }
    if (!recipients.length) { toast.error("Add at least one recipient"); return; }
    if (!bulkFields.some((f) => f.kind === "signature")) { toast.error("Place a signature field on the document — it goes on every copy"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/tenants/${tenantId}/sign-requests/${req.id}/send-batch`, { recipients, fields: bulkFields, ...opts });
      const n = r.data?.sent_count ?? recipients.length;
      const dropped = r.data?.dropped || [];
      toast.success(`Sending ${n} individual cop${n === 1 ? "y" : "ies"} — each person gets their own signing link.`);
      if (dropped.length) toast.warning(`${dropped.length} skipped (same phone/email as another recipient): ${dropped.slice(0, 3).join(", ")}${dropped.length > 3 ? "…" : ""}`);
      onSent();
    } catch (e) {
      console.error("batch send failed", e);
      toast.error(e?.response?.data?.detail || e?.message || "Couldn't send — your draft is saved, try again");
      setBusy(false);
    }
  };

  const bulkSlot = [{ name: "Each recipient", fields: bulkFields }];
  const setBulkSlot = (fn) => setBulkFields((prev) => {
    const next = typeof fn === "function" ? fn([{ name: "Each recipient", fields: prev }]) : fn;
    return next[0]?.fields || [];
  });

  return (
    <div className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 sm:p-8" data-testid="sig-wizard">
      <div className="bg-sand-50 w-full max-w-6xl rounded-3xl border border-sand-200 shadow-2xl p-6 sm:p-8 my-4">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="font-heading text-2xl font-black tracking-tight flex items-center gap-2"><PenLine className="h-6 w-6 text-redline-500" /> Advanced e-sign request</h2>
            <p className="text-sm text-ink/50 mt-0.5">
              {step === 1 ? "Step 1 — pick the document and who signs"
                : mode === "bulk" ? "Step 2 — choose recipients, place the fields once, send everyone their own copy"
                : "Step 2 — add signers, place their fields, send"}
            </p>
          </div>
          <button data-testid="sig-wizard-close" onClick={onClose} className="rounded-full p-2 hover:bg-sand-100"><X className="h-5 w-5" /></button>
        </div>

        {step === 1 && (
          <div className="max-w-xl space-y-4">
            <div>
              <label className="text-sm font-semibold">Title</label>
              <input data-testid="sig-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder='e.g. "2026 Budget Approval"'
                className="mt-1 w-full rounded-xl border border-sand-200 bg-white px-3 py-2.5 outline-none focus:border-ink/30" />
            </div>
            <div>
              <label className="text-sm font-semibold">Note to signers <span className="text-ink/40 font-normal">(optional)</span></label>
              <textarea data-testid="sig-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={2} placeholder="Please review and sign before Friday's meeting."
                className="mt-1 w-full rounded-xl border border-sand-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-ink/30" />
            </div>
            <div>
              <label className="text-sm font-semibold">Who signs?</label>
              <div className="mt-1 grid sm:grid-cols-2 gap-2">
                <button data-testid="sig-mode-single" onClick={() => setMode("single")}
                  className={`rounded-2xl border-2 p-3 text-left transition-colors ${mode === "single" ? "border-ink bg-white" : "border-sand-200 bg-white/60 hover:border-sand-300"}`}>
                  <div className="text-sm font-bold flex items-center gap-1.5"><Users className="h-4 w-4" /> One document, several signers</div>
                  <div className="text-xs text-ink/50 mt-0.5">Everyone signs the same copy — {V.grp()} approvals, contracts, resolutions.</div>
                </button>
                <button data-testid="sig-mode-bulk" onClick={() => setMode("bulk")}
                  className={`rounded-2xl border-2 p-3 text-left transition-colors ${mode === "bulk" ? "border-ink bg-white" : "border-sand-200 bg-white/60 hover:border-sand-300"}`}>
                  <div className="text-sm font-bold flex items-center gap-1.5"><Copy className="h-4 w-4" /> Each person signs their own copy</div>
                  <div className="text-xs text-ink/50 mt-0.5">One PDF, many recipients — amendments, acknowledgments, annual forms. Up to 500.</div>
                </button>
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold">Document (PDF)</label>
              <label data-testid="sig-upload" className={`mt-1 flex items-center gap-3 rounded-xl border-2 border-dashed px-4 py-4 cursor-pointer transition-colors ${file ? "border-emerald-300 bg-emerald-50" : "border-sand-300 bg-white hover:border-ink/30"}`}>
                <Upload className="h-5 w-5 text-ink/40" />
                <span className="text-sm font-semibold">{file ? file.name : "Upload a PDF"}</span>
                <input type="file" accept="application/pdf" className="hidden"
                  onChange={(e) => { setFile(e.target.files?.[0] || null); setPickedDoc(""); }} />
              </label>
              {docs.length > 0 && (
                <>
                  <p className="text-xs text-ink/40 my-2 text-center">— or pick from your Documents —</p>
                  <div className="rounded-xl border border-sand-200 bg-white divide-y divide-sand-100 max-h-44 overflow-y-auto">
                    {docs.map((d) => (
                      <button key={d.id} data-testid={`sig-pick-doc-${d.id}`}
                        onClick={() => { setPickedDoc(pickedDoc === d.id ? "" : d.id); setFile(null); if (!title) setTitle(d.title); }}
                        className={`w-full flex items-center gap-2 text-left px-3 py-2.5 text-sm hover:bg-sand-50 ${pickedDoc === d.id ? "bg-emerald-50" : ""}`}>
                        <FileText className="h-4 w-4 text-ink/40 shrink-0" />
                        <span className="font-semibold flex-1 truncate">{d.title}</span>
                        {pickedDoc === d.id && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            <button data-testid="sig-create-draft" onClick={createDraft} disabled={busy}
              className="rounded-full bg-ink text-white font-semibold px-6 py-3 hover:bg-ink-700 disabled:opacity-50 flex items-center gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronRight className="h-4 w-4" />} Continue — place fields
            </button>
          </div>
        )}

        {step === 2 && req && (
          <div className="space-y-5">
            <div data-testid="sig-reminder-bar" className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl border border-sand-200 bg-white px-4 py-3">
              <div className="flex items-center gap-2">
                <BellRing className="h-4 w-4 text-redline-500" />
                <span className="text-sm font-bold">Automatic reminders</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="font-semibold text-ink/60">every</span>
                <select data-testid="sig-cadence" value={cadence} onChange={(e) => setCadence(Number(e.target.value))}
                  className="rounded-lg border border-sand-200 bg-white px-2 py-1.5 text-sm font-semibold">
                  <option value={1}>day</option><option value={2}>2 days</option><option value={3}>3 days</option><option value={7}>week</option>
                </select>
                <span className="font-semibold text-ink/60">up to</span>
                <select data-testid="sig-max-reminders" value={maxRem} onChange={(e) => setMaxRem(Number(e.target.value))}
                  className="rounded-lg border border-sand-200 bg-white px-2 py-1.5 text-sm font-semibold">
                  {[0, 1, 2, 3, 5, 8, 10].map((n) => <option key={n} value={n}>{n} times</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2 text-sm sm:ml-auto">
                <CalendarClock className="h-4 w-4 text-redline-500" />
                <span className="font-semibold text-ink/60">Void if not signed by</span>
                <input data-testid="sig-expires" type="date" value={expires} min={todayISO()} onChange={(e) => setExpires(e.target.value)}
                  className="rounded-lg border border-sand-200 bg-white px-2 py-1.5 text-sm font-semibold" />
                {expires && <button data-testid="sig-expires-clear" onClick={() => setExpires("")} className="text-ink/30 hover:text-ink"><X className="h-4 w-4" /></button>}
              </div>
            </div>
            {mode === "single" && (
              <div data-testid="sig-notify-bar" className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-2xl border border-sand-200 bg-white px-4 py-3">
                <div className="flex items-center gap-2">
                  <BellRing className="h-4 w-4 text-redline-500" />
                  <span className="text-sm font-bold">Keep me posted</span>
                </div>
                <p className="text-xs text-ink/45 w-full sm:w-auto sm:flex-1 order-last sm:order-none">You&apos;re always told the moment everyone has signed. Add play-by-play below — it follows your notification preferences.</p>
                <label className="flex items-center gap-2 cursor-pointer text-sm font-semibold text-ink/70">
                  <Toggle testid="sig-notify-on-sign" on={notifyOnSign} onClick={() => setNotifyOnSign(!notifyOnSign)} /> Each time someone signs
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm font-semibold text-ink/70">
                  <Toggle testid="sig-notify-on-decline" on={notifyOnDecline} onClick={() => setNotifyOnDecline(!notifyOnDecline)} /> If someone declines
                </label>
              </div>
            )}
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="w-full lg:w-[340px] shrink-0 space-y-4">
                {mode === "bulk"
                  ? <RecipientPanel tenantId={tenantId} recipients={recipients} setRecipients={setRecipients} />
                  : <>
                    <SignerPanel tenantId={tenantId} signers={signers} setSigners={setSigners} selected={selected} setSelected={setSelected}
                      sequential={sequential} setSequential={setSequential} />
                    <ViewerPanel tenantId={tenantId} viewers={viewers} setViewers={setViewers} />
                  </>}
              </div>
              {mode === "bulk"
                ? <PlacementEditor tenantId={tenantId} req={req} signers={bulkSlot} setSigners={setBulkSlot} selected={0} setSelected={() => {}} singleSlot />
                : <PlacementEditor tenantId={tenantId} req={req} signers={signers} setSigners={setSigners} selected={selected} setSelected={setSelected} />}
            </div>
            <div className="flex flex-wrap items-center gap-4 border-t border-sand-200 pt-4">
              <div className="flex-1 text-xs text-ink/45">
                {expires ? <span className="flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" /> Voids automatically after {fmtDay(expires)} if anyone hasn&apos;t signed.</span> : null}
              </div>
              {mode === "bulk" ? (
                <button data-testid="sig-send-batch" onClick={sendBatch} disabled={busy}
                  className="rounded-full bg-redline-500 text-white font-bold px-7 py-3 hover:bg-redline-600 disabled:opacity-50 flex items-center gap-2">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Send {recipients.length || "…"} individual cop{recipients.length === 1 ? "y" : "ies"}
                </button>
              ) : (
                <button data-testid="sig-send" onClick={send} disabled={busy}
                  className="rounded-full bg-redline-500 text-white font-bold px-7 py-3 hover:bg-redline-600 disabled:opacity-50 flex items-center gap-2">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Send to {signers.length || "…"} signer{signers.length === 1 ? "" : "s"} by text & email
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Request detail (progress, remind, cancel, download) ─────────────────────
const CHILD_STATUS = {
  active: { label: "Waiting", cls: "text-amber-700", Icon: Clock },
  completed: { label: "Signed", cls: "text-emerald-700", Icon: CheckCircle2 },
  declined: { label: "Declined", cls: "text-danger-600", Icon: XCircle },
  expired: { label: "Expired", cls: "text-ink/40", Icon: Hourglass },
  canceled: { label: "Canceled", cls: "text-ink/40", Icon: Ban },
};

function RequestDetail({ tenantId, rid, onClose, onChanged, onResume, onOpen }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => {
    api.get(`/tenants/${tenantId}/sign-requests/${rid}`).then((r) => setD(r.data)).catch(() => onClose());
  }, [tenantId, rid, onClose]);
  useEffect(load, [load]);

  const remind = async (partyId) => {
    try {
      const r = await api.post(`/tenants/${tenantId}/sign-requests/${rid}/remind`, partyId ? { party_id: partyId } : {});
      toast.success(`Reminder sent to ${r.data.reminded} signer${r.data.reminded === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't remind"); }
  };
  const cancel = async () => {
    try { await api.post(`/tenants/${tenantId}/sign-requests/${rid}/cancel`); toast.success("Request canceled"); onChanged(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't cancel"); }
  };
  const deleteDraft = async () => {
    if (!window.confirm(`Delete the draft "${d?.title || "this document"}"? Nothing was sent, so it simply goes away.`)) return;
    try { await api.delete(`/tenants/${tenantId}/sign-requests/${rid}`); toast.success("Draft deleted"); onChanged(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't delete the draft"); }
  };
  const download = async (all) => {
    try {
      const r = await api.get(`/tenants/${tenantId}/sign-requests/${rid}/${all ? "download-all" : "download"}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = all ? `${d.title} (signed copies).zip` : `${d.title} (signed).pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  if (!d) return null;
  const st = STATUS[d.status] || STATUS.draft;
  const pending = (d.parties || []).filter((p) => p.status === "pending");
  const kids = d.children || [];
  const kidsDone = kids.filter((k) => k.status === "completed").length;
  const kidsOpen = kids.filter((k) => k.status === "active").length;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="sig-detail" className="max-w-lg bg-sand-50 border-sand-200 max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-black pr-6 break-words">{d.title}{d.recipient_name ? <span className="text-ink/40 font-semibold"> · {d.recipient_name}</span> : null}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${st.cls}`}>{d.is_batch && d.status === "completed" ? "ALL COPIES IN" : st.label}</span>
            {d.sequential && <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 flex items-center gap-1"><ListOrdered className="h-3 w-3" /> SIGNS IN ORDER</span>}
            {d.expires_at && d.status !== "completed" && (
              <span data-testid="sig-deadline" className={`text-[11px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1 ${d.status === "expired" ? "bg-sand-100 text-ink/50" : "bg-amber-50 text-amber-700"}`}>
                <CalendarClock className="h-3 w-3" /> {d.status === "expired" ? "EXPIRED" : "VOID AFTER"} {fmtDay(deadlineOf(d))}
              </span>
            )}
            <span className="text-xs text-ink/50">
              {d.status === "draft" ? "Created" : "Sent"} {fmtDT(d.sent_at || d.created_at)} by {d.created_by_name || "staff"}
            </span>
          </div>
          {d.status === "draft" && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5" data-testid="sig-draft-notice">
              <p className="text-sm font-semibold text-amber-900">This one was never sent.</p>
              <p className="text-xs text-amber-800/80 mt-0.5">Nobody has been asked to sign yet. Pick up where you left off — add signers, drop their fields on the document, and send it.</p>
              <div className="mt-2.5 flex flex-wrap gap-2">
                <button data-testid="sig-resume-draft" onClick={() => { onResume && onResume(d); onClose(); }}
                  className="rounded-full bg-ink text-white text-sm font-semibold px-4 py-2 hover:bg-ink-700 flex items-center gap-1.5">
                  <PenLine className="h-4 w-4" /> Finish and send
                </button>
                <button data-testid="sig-delete-draft" onClick={deleteDraft}
                  className="rounded-full border border-amber-300 bg-white text-sm font-semibold px-4 py-2 text-amber-900 hover:bg-amber-100 flex items-center gap-1.5">
                  <Trash2 className="h-4 w-4" /> Delete draft
                </button>
              </div>
            </div>
          )}
          {d.status === "expired" && !d.is_batch && (
            <div className="rounded-xl border border-sand-200 bg-white px-3 py-2.5 text-sm text-ink/70" data-testid="sig-expired-notice">
              The deadline passed before everyone signed — the signing links no longer work. Send a new request if you still need it.
            </div>
          )}
          {d.message && <p className="text-sm text-ink/60 bg-white rounded-xl border border-sand-200 px-3 py-2">“{d.message}”</p>}

          {d.is_batch ? (
            <div className="space-y-2" data-testid="sig-batch">
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-bold text-ink/40 uppercase tracking-wide flex items-center gap-1"><Copy className="h-3 w-3" /> {kids.length} individual copies</p>
                <p className="text-xs font-bold text-ink/60 tabular-nums">{kidsDone}/{kids.length} signed{kidsOpen ? ` · ${kidsOpen} waiting` : ""}</p>
              </div>
              <div className="h-1.5 rounded-full bg-sand-100 overflow-hidden">
                <div className="h-full rounded-full bg-emerald-500" style={{ width: `${kids.length ? Math.round((kidsDone / kids.length) * 100) : 0}%` }} />
              </div>
              <div className="rounded-xl border border-sand-200 bg-white divide-y divide-sand-100 max-h-[40vh] overflow-y-auto">
                {kids.map((k) => {
                  const cs = CHILD_STATUS[k.status] || CHILD_STATUS.active;
                  const p = k.party || {};
                  return (
                    <div key={k.id} data-testid={`sig-child-${k.id}`} className="flex items-center gap-2.5 px-3 py-2">
                      <cs.Icon className={`h-4 w-4 shrink-0 ${cs.cls}`} />
                      <button onClick={() => onOpen && onOpen(k.id)} className="flex-1 min-w-0 text-left">
                        <div className="text-sm font-semibold truncate">{p.name || k.recipient_name}</div>
                        <div className="text-xs text-ink/40 truncate">
                          {k.status === "completed" ? `Signed ${fmtDT(p.signed_at)}`
                            : k.status === "declined" ? `Declined${p.decline_reason ? ` — ${p.decline_reason}` : ""}`
                            : k.status === "active" ? (p.invited_at ? `Waiting · ${p.reminders_sent || 0} reminder${(p.reminders_sent || 0) === 1 ? "" : "s"}` : "Sending…")
                            : cs.label}
                        </div>
                      </button>
                      {k.status === "active" && p.invited_at && (
                        <button data-testid={`sig-remind-${p.id}`} onClick={() => remind(p.id)} className="rounded-full border border-sand-200 p-1.5 hover:bg-sand-50" title="Send a reminder now">
                          <BellRing className="h-3.5 w-3.5 text-ink/50" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {(d.parties || []).map((p, idx) => {
                const notYetInvited = p.status === "pending" && !p.invited_at;
                return (
                  <div key={p.id} data-testid={`sig-party-${p.id}`} className={`flex items-center gap-3 bg-white rounded-xl border border-sand-200 px-3 py-2.5 ${notYetInvited ? "opacity-60" : ""}`}>
                    {d.sequential && <span className="h-5 w-5 rounded-full bg-sand-100 text-[10px] font-black text-ink/50 flex items-center justify-center shrink-0">{idx + 1}</span>}
                    {p.status === "signed" ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
                      : p.status === "declined" ? <XCircle className="h-5 w-5 text-danger-500 shrink-0" />
                      : notYetInvited ? <Hourglass className="h-5 w-5 text-ink/30 shrink-0" />
                      : <Clock className="h-5 w-5 text-amber-500 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{p.name}</div>
                      <div className="text-xs text-ink/40 truncate">
                        {p.status === "signed" ? `Signed ${fmtDT(p.signed_at)}`
                          : p.status === "declined" ? `Declined${p.decline_reason ? ` — ${p.decline_reason}` : ""}`
                          : notYetInvited ? "Waiting their turn — invited after the signer before them"
                          : `Waiting · ${p.reminders_sent || 0} reminder${(p.reminders_sent || 0) === 1 ? "" : "s"} sent`}
                      </div>
                      {p.email_undeliverable && p.status === "pending" && (
                        <div data-testid={`sig-party-undeliverable-${p.id}`}
                          className="mt-1 inline-flex items-start gap-1.5 rounded-lg bg-redline-50 text-redline-700 px-2 py-1 text-[11px] font-semibold leading-snug">
                          <MailWarning className="h-3.5 w-3.5 shrink-0 mt-px" />
                          <span>Email bounced — {p.email} can&apos;t receive mail{p.phone ? ". They were texted the link instead." : ". No phone on file either, so they have no way in."}</span>
                        </div>
                      )}
                      {p.status === "signed" && (p.fields || []).some((f) => (f.kind === "text" || f.kind === "checkbox")) && (
                        <div className="text-xs text-ink/55 mt-0.5 space-x-2" data-testid={`sig-party-values-${p.id}`}>
                          {(p.fields || []).filter((f) => f.kind === "text" && (p.field_values || {})[f.id]).map((f) => (
                            <span key={f.id}><span className="font-semibold">{f.label || "Text"}:</span> {(p.field_values || {})[f.id]}</span>
                          ))}
                          {(p.fields || []).filter((f) => f.kind === "checkbox").map((f) => (
                            <span key={f.id}>{(p.field_values || {})[f.id] ? "☑" : "☐"} {f.label || "Checkbox"}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    {p.status === "pending" && p.invited_at && d.status === "active" && (
                      <button data-testid={`sig-remind-${p.id}`} onClick={() => remind(p.id)}
                        className="rounded-full border border-sand-200 p-2 hover:bg-sand-50" title="Send a reminder now">
                        <BellRing className="h-4 w-4 text-ink/50" />
                      </button>
                    )}
                  </div>
                );
              })}
              {(d.viewers || []).length > 0 && (
                <div className="rounded-xl border border-sand-200 bg-white/60 px-3 py-2" data-testid="sig-viewers-list">
                  <p className="text-[11px] font-bold text-ink/40 uppercase tracking-wide flex items-center gap-1 mb-1"><Eye className="h-3 w-3" /> Copied — gets the signed packet</p>
                  <div className="flex flex-wrap gap-1.5">
                    {d.viewers.map((v) => <span key={v.id} className="text-xs font-semibold rounded-full bg-sand-100 px-2.5 py-1">{v.name}</span>)}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-1">
            {((d.status === "active" && pending.length > 0) || (d.status === "batch" && kidsOpen > 0)) && (
              <button data-testid="sig-remind-all" onClick={() => remind(null)}
                className="rounded-full bg-ink text-white text-sm font-semibold px-4 py-2 hover:bg-ink-700 flex items-center gap-1.5">
                <BellRing className="h-4 w-4" /> Remind all pending
              </button>
            )}
            {d.is_batch ? (kidsDone > 0 && (
              <button data-testid="sig-download-all" onClick={() => download(true)}
                className="rounded-full bg-emerald-600 text-white text-sm font-semibold px-4 py-2 hover:bg-emerald-700 flex items-center gap-1.5">
                <Archive className="h-4 w-4" /> Download {kidsDone} signed cop{kidsDone === 1 ? "y" : "ies"} (zip)
              </button>
            )) : ((d.status === "completed" || (d.parties || []).some((p) => p.status === "signed")) && (
              <button data-testid="sig-download" onClick={() => download(false)}
                className="rounded-full bg-emerald-600 text-white text-sm font-semibold px-4 py-2 hover:bg-emerald-700 flex items-center gap-1.5">
                <Download className="h-4 w-4" /> {d.status === "completed" ? "Signed packet" : "Progress copy"}
              </button>
            ))}
            {(d.status === "active" || d.status === "batch") && (
              <button data-testid="sig-cancel" onClick={cancel}
                className="rounded-full border border-sand-200 text-sm font-semibold px-4 py-2 hover:bg-sand-50 text-ink/60 flex items-center gap-1.5">
                <Ban className="h-4 w-4" /> Cancel {d.is_batch ? "all open copies" : "request"}
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function Signatures() {
  const { tenantId, tenant } = useDash();
  const [reqs, setReqs] = useCachedState(`sigs:${tenantId}`, null);
  const [wizard, setWizard] = useState(false);
  const [quick, setQuick] = useState(false);
  const [detail, setDetail] = useState(null);
  const [resume, setResume] = useState(null);

  const load = useCallback(() => {
    if (!tenantId) return;
    api.get(`/tenants/${tenantId}/sign-requests`).then((r) => setReqs(r.data || [])).catch((e) => {
      setReqs([]);
      if (e.response?.status === 402) toast.error(`E-Sign isn't enabled for this ${V.lc("org")} yet.`);
    });
  }, [tenantId]);
  useEffect(load, [load]);

  return (
    <div className="space-y-8" data-testid="signatures-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <PageTitle title="E-Sign" about={<>Send any PDF for e-signature by text & email — {V.lcs("board")}, {V.lcs("person")} and vendors sign from their phone. Pick the document, name the signers, hit send: fields are pre-placed for you and reminders run themselves. You get a signed packet with a full audit certificate.</>} />
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button data-testid="sig-advanced" onClick={() => setWizard(true)} title="Everyone signs their own copy · initials, text and checkbox fields · place every field by hand"
            className="rounded-full border border-sand-300 bg-white text-ink font-semibold px-4 py-2.5 hover:border-ink text-sm">
            Advanced
          </button>
          <button data-testid="sig-new" onClick={() => setQuick(true)}
            className="rounded-full bg-redline-500 text-white font-bold px-5 py-2.5 hover:bg-redline-600 flex items-center gap-2">
            <Plus className="h-4 w-4" /> Send for signature
          </button>
        </div>
      </div>

      {reqs === null ? (
        <div className="text-center py-16 text-ink/40"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
      ) : reqs.length === 0 ? (
        <div data-testid="sig-empty" className="bg-white rounded-3xl border border-sand-200 p-14 text-center flex flex-col items-center gap-3">
          <PenLine className="h-10 w-10 text-sand-300" />
          <p className="font-heading font-extrabold text-lg">Nothing out for signature yet</p>
          <p className="text-sm text-ink/50 max-w-sm">Pick a PDF, name who signs, send. We place the fields, chase the signatures and hand you the signed packet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 min-w-0">
          {reqs.map((r) => {
            const st = STATUS[r.status] || STATUS.draft;
            const pct = r.total ? Math.round((r.signed / r.total) * 100) : 0;
            return (
              <button key={r.id} data-testid={`sig-card-${r.id}`} onClick={() => setDetail(r.id)}
                className="w-full min-w-0 bg-white rounded-2xl border border-sand-200 px-4 sm:px-5 py-4 text-left hover:border-ink/25 transition-colors flex items-center gap-3 sm:gap-4">
                {r.is_batch ? <Copy className="h-8 w-8 text-ink/25 shrink-0" /> : <FileText className="h-8 w-8 text-ink/25 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                    <span className="font-heading font-extrabold min-w-0 max-w-full break-words">{r.title}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${st.cls}`}>{r.is_batch && r.status === "completed" ? "ALL COPIES IN" : st.label}</span>
                    {r.expires_at && r.status !== "expired" && r.status !== "completed" && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-sand-100 text-ink/50 flex items-center gap-1"><CalendarClock className="h-3 w-3" /> by {fmtDay(deadlineOf(r))}</span>
                    )}
                  </div>
                  <div className="text-xs text-ink/40 mt-0.5">{fmtDT(r.sent_at || r.created_at)}{r.declined ? ` · ${r.declined} ${r.is_batch ? "not signed" : "declined"}` : ""}</div>
                </div>
                {r.status !== "draft" && (
                  <div className="w-36 shrink-0 hidden sm:block">
                    <div className="text-xs font-bold text-ink/60 mb-1 text-right">{r.signed}/{r.total} {r.is_batch ? "copies" : "signed"}</div>
                    <div className="h-1.5 rounded-full bg-sand-100 overflow-hidden">
                      <div className={`h-full rounded-full ${r.status === "completed" ? "bg-emerald-500" : "bg-amber-400"}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                )}
                <ChevronRight className="h-4 w-4 text-ink/30 shrink-0" />
              </button>
            );
          })}
        </div>
      )}

      {quick && <ESignQuickSend tenantId={tenantId} tenant={tenant}
        onClose={() => setQuick(false)}
        onSent={() => { setQuick(false); load(); }}
        onAdvanced={(draft) => { setQuick(false); setResume(draft); setWizard(true); }} />}
      {wizard && <NewRequestWizard tenantId={tenantId} tenant={tenant} resumeDraft={resume}
        onClose={() => { setWizard(false); setResume(null); }}
        onSent={() => { setWizard(false); setResume(null); load(); }} />}
      {detail && <RequestDetail tenantId={tenantId} rid={detail} onClose={() => setDetail(null)} onChanged={load}
        onOpen={(id) => setDetail(id)}
        onResume={(d) => { setResume(d); setWizard(true); }} />}
    </div>
  );
}
